/**
 * The per-rank reward ladder, shared by the manual and automatic raid
 * profile pages so there is one prize editor rather than two that drift.
 *
 * Two spec traps the UI has to make obvious:
 *   - a numbered rank REPLACES Default, it never stacks on top of it;
 *   - eggs cannot ride in the item list. Handed out as a plain ItemID an egg
 *     arrives with no pal inside and never hatches, so they are a separate
 *     list that becomes an EggID + PalTemplate drop.
 */
import { Trash2, Plus, Info } from "lucide-react";
import { ItemPicker, EggPicker } from "@/components/admin/ItemPicker";

const RANK_LABEL = {
  "1": "1st — top damage",
  "2": "2nd",
  "3": "3rd",
  Default: "Everyone else",
};

const BONUS_LABEL = { exp: "EXP", tech: "Tech", ancient_tech: "Ancient" };

/**
 * Column widths are deliberate. Qty holds 6-7 digit Money values and was
 * clipping to a single character at 52px; the number spinner stole another
 * ~16px so it is suppressed. Below 640px the picker gets its own line,
 * where sharing a row collapsed it to "G…" instead of "Gold Coin".
 */
const NO_SPIN =
  "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none " +
  "[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0";
const ROW =
  "grid grid-cols-[minmax(0,1fr)_48px_44px_22px] " +
  "sm:grid-cols-[minmax(0,1fr)_80px_80px_52px_22px] gap-1 items-center";
const HEAD = ROW.replace("grid ", "hidden sm:grid ");
const PICK = "col-span-4 sm:col-span-1 min-w-0";
const NUM = `input input-sm w-full px-1 text-[11px] text-right ${NO_SPIN}`;

function RowHeader({ first }) {
  return (
    <li className={`${HEAD} text-[9px] uppercase tracking-wider text-[var(--dim)]`}>
      <span>{first}</span>
      <span className="text-center">Qty</span>
      <span className="text-center">to</span>
      <span className="text-center">%</span>
      <span />
    </li>
  );
}

const optNum = (e) => {
  const v = parseInt(e.target.value, 10);
  return Number.isFinite(v) ? v : null;
};

export function RaidPrizeLadder({ prizes, onChange, testPrefix = "summon" }) {
  const patchPrize = (rank, key, value) =>
    onChange({ ...prizes, [rank]: { ...prizes[rank], [key]: value } });

  const patchRow = (rank, list, idx, key, value) => {
    const rows = [...(prizes[rank]?.[list] || [])];
    rows[idx] = { ...rows[idx], [key]: value };
    patchPrize(rank, list, rows);
  };

  const removeRow = (rank, list, idx) =>
    patchPrize(rank, list, (prizes[rank]?.[list] || []).filter((_, j) => j !== idx));

  return (
    <div data-testid={`${testPrefix}-prizes`}>
      {/* Trap: authored per place, never as a bonus on top of Default. */}
      <p className="mb-3 text-[11px] text-[var(--muted)] flex gap-1.5 leading-snug">
        <Info size={11} className="mt-0.5 flex-shrink-0 text-[var(--teal)]" />
        <span>
          Each rank is the <strong>complete</strong> reward for that place. A rank
          replaces Default rather than adding to it.{" "}
          <span className="text-[var(--dim)]">
            Qty/to gives a random range; % is the chance it drops at all (blank = always).
          </span>
        </span>
      </p>

      {/* Ranks go two-up only at xl: at md the cards squeeze the number boxes. */}
      <div className="grid gap-3 xl:grid-cols-2">
        {Object.keys(RANK_LABEL).map((rank) => {
          const p = prizes?.[rank] || { items: [], eggs: [], bonus: [] };
          return (
            <div key={rank} className="rounded border border-white/10 p-3"
              data-testid={`${testPrefix}-rank-${rank}`}>
              <p className="text-xs uppercase tracking-wider text-[var(--bone)] mb-2">
                {RANK_LABEL[rank]}
              </p>

              <div className="flex gap-2 mb-2">
                {[["exp", "EXP"], ["tech", "Tech"], ["ancient_tech", "Ancient"]].map(
                  ([key, label]) => (
                    <label key={key} className="text-[10px] text-[var(--muted)] flex-1 min-w-0">
                      {label}
                      <input
                        type="number" min="0"
                        value={p[key] ?? 0}
                        onChange={(e) => patchPrize(rank, key, parseInt(e.target.value, 10) || 0)}
                        className={NUM}
                        data-testid={`${testPrefix}-${rank}-${key}`}
                      />
                    </label>
                  ),
                )}
              </div>

              <ul className="space-y-1.5">
                {(p.items || []).length > 0 && <RowHeader first="Item" />}
                {(p.items || []).map((it, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={`${rank}-${i}`} className={ROW}>
                    <div className={PICK}>
                      <ItemPicker
                        value={it.id}
                        onChange={(v) => patchRow(rank, "items", i, "id", v)}
                        testId={`${testPrefix}-${rank}-item-${i}`}
                      />
                    </div>
                    <input type="number" min="1" value={it.count}
                      onChange={(e) => patchRow(rank, "items", i, "count", parseInt(e.target.value, 10) || 1)}
                      className={NUM} data-testid={`${testPrefix}-${rank}-count-${i}`} />
                    <input type="number" min="1" placeholder="—" value={it.count_max ?? ""}
                      onChange={(e) => patchRow(rank, "items", i, "count_max", optNum(e))}
                      className={NUM} data-testid={`${testPrefix}-${rank}-countmax-${i}`} />
                    <input type="number" min="0" max="100" placeholder="—" value={it.chance ?? ""}
                      onChange={(e) => patchRow(rank, "items", i, "chance", optNum(e))}
                      className={NUM} data-testid={`${testPrefix}-${rank}-chance-${i}`} />
                    <button onClick={() => removeRow(rank, "items", i)}
                      className="px-1.5 text-[var(--muted)] hover:text-[var(--error)]"
                      aria-label="Remove item"
                      data-testid={`${testPrefix}-${rank}-remove-${i}`}>
                      <Trash2 size={13} />
                    </button>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => patchPrize(rank, "items", [...(p.items || []), { id: "", count: 1 }])}
                className="mt-2 text-[11px] text-[var(--teal)] hover:underline flex items-center gap-1"
                data-testid={`${testPrefix}-${rank}-add`}>
                <Plus size={11} /> Add item
              </button>

              <p className="text-[10px] uppercase tracking-wider text-[var(--muted)] mt-3 mb-1">
                Egg prizes
              </p>
              <ul className="space-y-1.5">
                {(p.eggs || []).length > 0 && <RowHeader first="Egg" />}
                {(p.eggs || []).map((eg, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={`${rank}-egg-${i}`} className={ROW}>
                    <div className={PICK}>
                      <EggPicker
                        value={eg.id}
                        onChange={(v) => patchRow(rank, "eggs", i, "id", v)}
                        testId={`${testPrefix}-${rank}-egg-${i}`}
                      />
                    </div>
                    <input type="number" min="1" max="10" value={eg.count}
                      onChange={(e) => patchRow(rank, "eggs", i, "count", parseInt(e.target.value, 10) || 1)}
                      className={NUM} data-testid={`${testPrefix}-${rank}-eggcount-${i}`} />
                    <input type="number" min="1" max="10" placeholder="—" value={eg.count_max ?? ""}
                      onChange={(e) => patchRow(rank, "eggs", i, "count_max", optNum(e))}
                      className={NUM} data-testid={`${testPrefix}-${rank}-eggcountmax-${i}`} />
                    <input type="number" min="0" max="100" placeholder="—" value={eg.chance ?? ""}
                      onChange={(e) => patchRow(rank, "eggs", i, "chance", optNum(e))}
                      className={NUM} data-testid={`${testPrefix}-${rank}-eggchance-${i}`} />
                    <button onClick={() => removeRow(rank, "eggs", i)}
                      className="px-1.5 text-[var(--muted)] hover:text-[var(--error)]"
                      aria-label="Remove egg"
                      data-testid={`${testPrefix}-${rank}-eggremove-${i}`}>
                      <Trash2 size={13} />
                    </button>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => patchPrize(rank, "eggs", [...(p.eggs || []), { id: "", count: 1 }])}
                className="mt-2 text-[11px] text-amber-300 hover:underline flex items-center gap-1"
                data-testid={`${testPrefix}-${rank}-eggadd`}>
                <Plus size={11} /> Add egg
              </button>
              {(p.eggs || []).length > 0 && (
                <p className="text-[10px] text-[var(--muted)] mt-1 leading-snug">
                  Each egg rolls a real pal + level from our hatch pools when the
                  raid spawns, so it always hatches.
                </p>
              )}

              {/* The EXP/Tech/Ancient boxes above are always granted —
                  PalDefender's shorthands take no Chance. An optional
                  progression reward has to be its own drop entry. */}
              <p className="text-[10px] uppercase tracking-wider text-[var(--muted)] mt-3 mb-1">
                Chance-based bonus
              </p>
              <ul className="space-y-1.5">
                {(p.bonus || []).length > 0 && <RowHeader first="Type" />}
                {(p.bonus || []).map((b, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={`${rank}-bonus-${i}`} className={ROW}>
                    <select value={b.type || "exp"}
                      onChange={(e) => patchRow(rank, "bonus", i, "type", e.target.value)}
                      className="input input-sm w-full px-1 text-[11px] col-span-4 sm:col-span-1"
                      data-testid={`${testPrefix}-${rank}-bonustype-${i}`}>
                      {Object.entries(BONUS_LABEL).map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                    <input type="number" min="1" value={b.count ?? 1}
                      onChange={(e) => patchRow(rank, "bonus", i, "count", parseInt(e.target.value, 10) || 1)}
                      className={NUM} data-testid={`${testPrefix}-${rank}-bonuscount-${i}`} />
                    <input type="number" min="1" placeholder="—" value={b.count_max ?? ""}
                      onChange={(e) => patchRow(rank, "bonus", i, "count_max", optNum(e))}
                      className={NUM} data-testid={`${testPrefix}-${rank}-bonuscountmax-${i}`} />
                    <input type="number" min="0" max="100" placeholder="—" value={b.chance ?? ""}
                      onChange={(e) => patchRow(rank, "bonus", i, "chance", optNum(e))}
                      className={NUM} data-testid={`${testPrefix}-${rank}-bonuschance-${i}`} />
                    <button onClick={() => removeRow(rank, "bonus", i)}
                      className="px-1.5 text-[var(--muted)] hover:text-[var(--error)]"
                      aria-label="Remove bonus"
                      data-testid={`${testPrefix}-${rank}-bonusremove-${i}`}>
                      <Trash2 size={13} />
                    </button>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => patchPrize(rank, "bonus",
                  [...(p.bonus || []), { type: "exp", count: 1000, chance: 25 }])}
                className="mt-2 text-[11px] text-[var(--teal)] hover:underline flex items-center gap-1"
                data-testid={`${testPrefix}-${rank}-bonusadd`}>
                <Plus size={11} /> Add chance bonus
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
